import React, {useState, useEffect, useContext, useMemo, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal.mjs';
import "../css/FileDetailsModal.css";
import * as FileService from '../services/FileService.mjs';
import * as AudioSplitService from '../services/AudioSplitService.mjs';
import * as TranscriptionService from '../services/TranscriptionService.mjs';
import ClosingX from './ClosingX.jsx';
import { getAllAvailableTags } from "../services/TagService.mjs";
import FilePreview from "./shared/FilePreview.jsx";
import apiClient from "../config/axiosConfig.js";
import FileVersionHistory from './FileVersionHistory.jsx';
import FileLockBadge from './FileLockBadge.jsx';
import MetadataEditor from './MetadataEditor.jsx';
import FileProjects from './project/FileProjects.jsx';
import NotesPanel from './NotesPanel.jsx';
import DenoiseModal from "./DenoiseModal.jsx";
import Tabs, { tabPanelProps } from './common/Tabs.jsx';
import ActionMenu from './common/ActionMenu.jsx';
import InlineBanner from './common/InlineBanner.jsx';
import FileTagsSection from './file/FileTagsSection.jsx';
import FileDerivationSection from './file/FileDerivationSection.jsx';
import SimilarAssetsSection from './file/SimilarAssetsSection.jsx';
import TranscriptPanel from './file/TranscriptPanel.jsx';
import {
    IconAlert, IconCheck, IconCopy, IconDownload, IconImage, IconLink, IconPencil,
    IconRefresh, IconScissors, IconSparkle, IconTrash, KIND_ICONS,
} from './file/icons.jsx';
import { WorkspaceContext } from "../context/WorkspaceContext.js";
import { useConfirm } from "../context/ConfirmContext.jsx";
import {
    canTranscribe, headerFacts, kindOf, technicalRows, TECHNICAL_KEYS,
} from '../utils/fileFacts.js';

// Keys the metadata editor never offers as free-text fields, because something
// else on this screen already owns them.
//
// `name` is the title in the header, and renaming happens there — it is the
// first thing you look at and the most-edited field on the record, so burying a
// duplicate of it four rows into a property list gave two places to do one job.
//
// `projects` and `projectIds` are the retired free-text list and the real
// membership array. The free-text field was cut over to Project entities (D1);
// leaving its row here offered to edit a field nothing reads, and a document
// written before the migration would still show a stale list beside the true
// one. The Projects section below is the single answer to "which projects is
// this file in?".
//
// The probed/derived fields (size, duration, folderPath, codecs …) join them:
// they describe the bytes, so hand-editing them only ever produced a record
// that disagreed with the file. They are rendered read-only under "Technical
// details" instead — see utils/fileFacts.
//
// `sourceFileId`/`sourceStart`/`sourceEnd` are the audio splitter's retired
// provenance keys. Documents written before derivation links existed still carry
// them, and they rendered as an editable "Source file id" text box that changed
// nothing — the backend now refuses writes to all three. The Source & exports
// section below is the single answer to "where did this file come from?".
const HIDDEN_METADATA_KEYS = [
    'name', 'tags', 'transcription', '_id', '__v', 'projects', 'projectIds',
    'peaksGcsId', 'aiTags', 'aiDescription',
    'sourceFileId', 'sourceStart', 'sourceEnd',
    ...TECHNICAL_KEYS,
];

// Role ranking for audio-tool gating (mirrors SplitPage).
const ROLE_ORDER = ['viewer', 'uploader', 'editor', 'admin', 'owner'];
const rankRole = (role) => ROLE_ORDER.indexOf(role);

// Above this size we ask the backend for precomputed waveform peaks rather than
// letting the browser download + decode the whole file just to draw the preview
// waveform. Keep in sync with SplitPage's PEAKS_THRESHOLD_BYTES.
const PEAKS_THRESHOLD_BYTES = 25 * 1024 * 1024;

const TAB_IDS = 'fd';

const FileDetailsModal = ({ file: initialFile, onClose, onFilesChanged, siblings = [], onNavigate }) => {
    const [file, setFile] = useState(initialFile);
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { activeWorkspaceRole } = useContext(WorkspaceContext);
    const [showDenoiseModal, setShowDenoiseModal] = useState(false);
    const [allTags, setAllTags] = useState([]);
    const [copied, setCopied] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [contentUnavailable, setContentUnavailable] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [versionCount, setVersionCount] = useState(null);
    const [noteCount, setNoteCount] = useState(null);
    const [showTechnical, setShowTechnical] = useState(false);
    // Inline rename, driven from the header title.
    const [renaming, setRenaming] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [nameError, setNameError] = useState(null);
    const renameRef = useRef(null);
    // Precomputed waveform peaks for large audio files (streaming preview).
    const [peaks, setPeaks] = useState(null);
    const [peaksDuration, setPeaksDuration] = useState(0);
    const [peaksLoading, setPeaksLoading] = useState(false);

    // ── Stepping through the list this modal was opened from ─────────────────
    //
    // Reviewing forty renders used to cost forty open-close cycles: the modal
    // knew one file and nothing about where it sat. The host hands over the list
    // it opened from, and ← / → walk it.
    //
    // The ends DISABLE rather than wrap. Looping silently back to the first file
    // reads as "nothing happened" at exactly the moment someone is counting on
    // position to tell them they're done.
    const position = useMemo(() => {
        if (!onNavigate || !Array.isArray(siblings) || siblings.length < 2) return null;
        const index = siblings.findIndex((f) => f?.id === file?.id);
        if (index === -1) return null;
        return {
            index,
            current: index + 1,
            total: siblings.length,
            hasPrev: index > 0,
            hasNext: index < siblings.length - 1,
        };
    }, [siblings, file?.id, onNavigate]);

    const step = useCallback((delta) => {
        if (!position) return;
        const next = siblings[position.index + delta];
        if (next) onNavigate(next);
    }, [position, siblings, onNavigate]);

    useEffect(() => {
        if (!position) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            const el = e.target;
            // Not while typing — the header's inline rename, a note, a tag box.
            if (el?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el?.tagName)) return;
            // Not while one of this modal's own dialogs sits on top of it.
            if (document.querySelectorAll('[role="dialog"]').length > 1) return;
            e.preventDefault();
            step(e.key === 'ArrowLeft' ? -1 : 1);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [position, step]);

    // Keyed on the file's ID, not the prop's object identity.
    //
    // Hosts open this modal with whatever document they happened to be holding —
    // a search hit, a project file list, a resolved layout entry — and then
    // re-render with a fresh copy of it whenever their own list reloads. Adopting
    // that copy would undo everything the modal has fetched since, so a new
    // object for the same id is deliberately ignored; only a genuinely different
    // file is adopted, and that one gets a full reload.
    const initialFileId = initialFile?.id;
    useEffect(() => {
        setFile(initialFile);
        setActiveTab('details');
        setVersionCount(null);
        setNoteCount(null);
        setShowTechnical(false);
        setRenaming(false);
        setNameError(null);
        fetchTags();
        buildAuthUrls(initialFileId);
        refreshFileData(false, initialFileId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFileId]);

    // Load precomputed peaks for large audio files so the preview streams instead
    // of downloading + decoding the whole recording. Re-runs whenever the file's
    // content changes (new version via denoise, or a version restore) so the
    // waveform always matches what's playing. Small files / non-audio skip this
    // and use WaveSurfer's in-browser decode (fast enough, no server round-trip).
    useEffect(() => {
        let cancelled = false;
        const mimetype = file.metadata?.mimetype || '';
        const size = Number(file.metadata?.size || 0);

        if (!mimetype.startsWith('audio') || size <= PEAKS_THRESHOLD_BYTES) {
            setPeaks(null);
            setPeaksDuration(0);
            setPeaksLoading(false);
            return;
        }

        setPeaks(null);
        setPeaksDuration(0);
        setPeaksLoading(true);
        (async () => {
            try {
                const { peaks: p, duration: d } = await AudioSplitService.getPeaks(file.id);
                if (cancelled) return;
                setPeaks(p);
                setPeaksDuration(d);
            } catch (err) {
                // Best-effort: fall back to in-browser decode if peaks are
                // unavailable (e.g. generation failed) rather than blocking playback.
                if (cancelled) return;
                console.warn('Peaks unavailable, falling back to direct decode:', err);
                setPeaks(null);
                setPeaksDuration(0);
            } finally {
                if (!cancelled) setPeaksLoading(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file.id, file.currentVersionId, file.metadata?.size]);

    // Open the rename field with the stem selected, not the whole value: renaming
    // almost never means changing ".wav", and a plain select-all leaves the
    // extension one keystroke from being typed away.
    useEffect(() => {
        if (!renaming) return;
        const input = renameRef.current;
        if (!input) return;
        input.focus();
        const dot = input.value.lastIndexOf('.');
        input.setSelectionRange(0, dot > 0 ? dot : input.value.length);
    }, [renaming]);

    // Clear the "Copied!" acknowledgement without leaving a timer behind if the
    // modal closes first.
    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(null), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const buildAuthUrls = async (fileId) => {
        // SEC-7: the download/"Copy link" URL is a signed GCS URL with NO token.
        // A 404 here means the file's record/content is missing (e.g. it was
        // deleted but is still listed in the search index) — handle it cleanly
        // instead of leaving an uncaught promise rejection.
        try {
            const dlUrl = await FileService.getSignedDownloadUrl(fileId);
            setDownloadUrl(dlUrl);
            setContentUnavailable(false);
        } catch (err) {
            console.warn('Could not get a signed download URL:', err);
            setDownloadUrl(null);
            setContentUnavailable(true);
        }
    };

    const fetchTags = async () => {
        try {
            const tags = await getAllAvailableTags();
            setAllTags(Array.isArray(tags) ? tags : []);
        } catch (error) {
            console.error("Failed to fetch tags:", error);
            setAllTags([]);
        }
    };

    const refreshFileData = async (notifyParent = true, targetId = file.id) => {
        setIsLoading(true);
        try {
            const response = await apiClient.get(`/api/files/get-one?id=${targetId}`);
            setFile(response.data);

            if (notifyParent && onFilesChanged) {
                onFilesChanged();
            }

            fetchTags();
        } catch (error) {
            console.error("Failed to refresh file data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Refresh after something wrote a NEW VERSION of this file.
     *
     * Anything that changes which object the file points at (`gcsId`) also
     * invalidates the signed download URL minted at mount — so re-pull the doc
     * AND re-sign. Without the second half the preview and "Copy link" keep
     * serving the previous version's bytes, with nothing on screen saying so.
     */
    const reloadFileAndUrls = () => {
        refreshFileData();
        buildAuthUrls(file.id);
    };

    const handleRequestTranscription = async () => {
        await TranscriptionService.requestFileTranscription(file.id);
        await refreshFileData();
    };

    const handleRequestThumbnailRegeneration = async () => {
        await FileService.requestThumbnailRegeneration(file.id);
        refreshFileData();
    };

    const deleteFile = async () => {
        const ok = await confirm({
            title: "Delete file?",
            message: `“${file.metadata?.name || 'This file'}” and all of its versions will be removed. This cannot be undone.`,
            confirmText: "Delete",
            cancelText: "Cancel",
            danger: true,
        });
        if (ok) {
            FileService.deleteFile(file.id).then(() => {
                if (onFilesChanged) onFilesChanged();
                if (onClose) onClose();
            });
        }
    };

    // Downloading a multi-GB asset is not instant, and the old fire-and-forget
    // call gave no sign it had started.
    const downloadFile = async () => {
        setIsDownloading(true);
        try {
            const blob = await FileService.fetchFileBlob(file.id);
            FileService.saveBlob(blob, file.metadata?.name || 'download');
        } catch (error) {
            // Surfaced globally by the axios interceptor (notification center).
            console.error('Download failed:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSplit = () => {
        if (onClose) onClose();
        navigate(`/split/${file.id}`);
    };

    const handleCopy = async (text, type) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(type);
        } catch (err) {
            console.error('Could not copy to the clipboard:', err);
        }
    };

    // --- Metadata (persisted via the MetadataEditor component) ---

    const handleMetadataSave = async (key, value) => {
        await FileService.updateFileMetadata(file.id, { [key]: value });
        await refreshFileData();
    };

    // --- Rename (the header title is the editor) ---

    const startRename = () => {
        setDraftName(file.metadata?.name || '');
        setNameError(null);
        setRenaming(true);
    };

    const cancelRename = () => {
        setRenaming(false);
        setNameError(null);
    };

    /**
     * Enter and blur both commit; Escape reverts.
     *
     * An empty value is only an error when it was deliberate (Enter). On blur it
     * reverts instead — clicking away from a field you emptied by accident should
     * not trap you in it with a message.
     */
    const commitRename = async (fromBlur = false) => {
        if (savingName) return;
        const next = draftName.trim();
        const current = file.metadata?.name || '';

        if (!next) {
            if (fromBlur) { cancelRename(); return; }
            setNameError('A file name can’t be empty.');
            return;
        }
        if (next === current) { cancelRename(); return; }

        setSavingName(true);
        setNameError(null);
        try {
            await handleMetadataSave('name', next);
            setRenaming(false);
        } catch (error) {
            // Reported globally by the axios interceptor. Stay in edit mode so the
            // typed name isn't lost to a failed request.
            console.error('Rename failed:', error);
        } finally {
            setSavingName(false);
        }
    };

    const handleMetadataRemove = async (key) => {
        await FileService.updateFileMetadata(file.id, { [key]: null });
        await refreshFileData();
    };

    const handleCaptureThumbnail = async (dataUrl) => {
        try {
            setIsLoading(true);
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            const fileToUpload = new File([blob], "thumbnail.png", { type: "image/png" });

            const formData = new FormData();
            formData.append('file', fileToUpload);

            await apiClient.post(`/api/files/thumbnail/upload?id=${file.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                errorTitle: 'Failed to update thumbnail',
            });

            // Pull the new thumbnail's timestamp onto the record (and into the
            // grid behind the modal) — that stamp is what versions the thumbnail
            // URL, so without it the capture wouldn't show until a hard reload.
            await refreshFileData();
        } catch (error) {
            // Surfaced globally by the axios interceptor (notification center).
            console.error("Error uploading thumbnail:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const { metadata } = file;
    const name = metadata.name || "Untitled file";
    const mimetype = metadata.mimetype || '';
    const kind = kindOf(mimetype, name);
    const KindIcon = KIND_ICONS[kind] || KIND_ICONS.file;
    const isAudio = mimetype.startsWith('audio');
    const isImage = mimetype.startsWith('image');
    const canEdit = rankRole(activeWorkspaceRole) >= rankRole('editor');
    const isOwner = activeWorkspaceRole === 'owner';

    const facts = useMemo(() => headerFacts(file), [file]);
    const technical = useMemo(() => technicalRows(metadata), [metadata]);
    const transcript = metadata.transcription?.text || '';
    const showTranscriptTab = canTranscribe(mimetype) || !!transcript;

    const onVersionsLoaded = useCallback((count) => setVersionCount(count), []);
    const onNotesLoaded = useCallback((count) => setNoteCount(count), []);

    const tabs = [
        { id: 'details', label: 'Details' },
        { id: 'versions', label: 'Versions', count: versionCount == null ? undefined : versionCount + 1 },
        ...(showTranscriptTab
            ? [{ id: 'transcript', label: isImage ? 'Description' : 'Transcript', dot: !!transcript }]
            : []),
        { id: 'notes', label: 'Notes', count: noteCount ?? undefined },
        // Last, and mounted lazily below: it is the only tab that costs a
        // similarity search on open, so it should not run for someone who came
        // here to rename a file.
        { id: 'similar', label: 'Similar', title: 'Find assets that look like this one' },
    ];

    // The tab list is content-dependent (no transcript tab for a .zip), so a
    // stale selection has to fall back rather than render nothing.
    const currentTab = tabs.some((t) => t.id === activeTab) ? activeTab : 'details';

    const menuItems = [
        {
            id: 'copy-id',
            label: 'Copy file ID',
            icon: <IconCopy />,
            onSelect: () => handleCopy(file.id, 'id'),
        },
        {
            id: 'thumbnail',
            label: 'Regenerate thumbnail',
            description: 'Re-render the browser preview image',
            icon: <IconImage />,
            onSelect: handleRequestThumbnailRegeneration,
        },
        {
            id: 'refresh',
            label: isLoading ? 'Refreshing…' : 'Refresh from server',
            icon: <IconRefresh />,
            onSelect: () => refreshFileData(),
        },
        ...(isOwner
            ? [{
                id: 'delete',
                label: 'Delete file',
                icon: <IconTrash />,
                danger: true,
                separatorBefore: true,
                onSelect: deleteFile,
            }]
            : []),
    ];

    return (
        <>
            <Modal className="fileDetailsModal" onClose={onClose}>
                <header className="fd-head">
                    <div className="fd-identity">
                        <span className={`fd-kind fd-kind--${kind}`} aria-hidden="true"><KindIcon width="18" height="18" /></span>
                        <div className="fd-titles">
                            <h1 className="fd-name-row">
                                {renaming ? (
                                    <input
                                        ref={renameRef}
                                        type="text"
                                        className="fd-name-input"
                                        value={draftName}
                                        aria-label="File name"
                                        aria-invalid={!!nameError}
                                        disabled={savingName}
                                        onChange={(e) => setDraftName(e.target.value)}
                                        onBlur={() => commitRename(true)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                commitRename();
                                            } else if (e.key === 'Escape') {
                                                // Swallow it — Escape here means "stop
                                                // renaming", not "close the dialog".
                                                e.preventDefault();
                                                e.stopPropagation();
                                                cancelRename();
                                            }
                                        }}
                                    />
                                ) : canEdit ? (
                                    <button
                                        type="button"
                                        className="fd-name fd-name--editable"
                                        onClick={startRename}
                                        title={`${name} — click to rename`}
                                    >
                                        <span className="fd-name-text">{name}</span>
                                        <IconPencil className="fd-name-pencil" width="14" height="14" />
                                    </button>
                                ) : (
                                    <span className="fd-name" title={name}>{name}</span>
                                )}
                            </h1>

                            {nameError && <p className="fd-name-error" role="alert">{nameError}</p>}

                            <div className="fd-facts">
                                {facts.map((fact) => (
                                    <span key={fact.key} className="fd-fact" title={fact.title}>{fact.value}</span>
                                ))}
                                <button
                                    type="button"
                                    className="fd-idchip"
                                    onClick={() => handleCopy(file.id, 'id')}
                                    title={`Copy file ID — ${file.id}`}
                                >
                                    {copied === 'id' ? <IconCheck width="12" height="12" /> : <IconCopy width="12" height="12" />}
                                    <span className="fd-idchip-text">{copied === 'id' ? 'Copied' : file.id}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="fd-head-actions">
                        <button
                            type="button"
                            className="qd-btn qd-btn--primary qd-btn--sm"
                            onClick={downloadFile}
                            disabled={isDownloading || contentUnavailable}
                        >
                            <IconDownload />
                            <span className="fd-action-label">{isDownloading ? 'Preparing…' : 'Download'}</span>
                        </button>

                        <button
                            type="button"
                            className="qd-btn qd-btn--secondary qd-btn--sm"
                            onClick={() => handleCopy(downloadUrl, 'link')}
                            disabled={!downloadUrl}
                            title="Copy a temporary signed link to this file"
                        >
                            {copied === 'link' ? <IconCheck /> : <IconLink />}
                            <span className="fd-action-label">{copied === 'link' ? 'Copied' : 'Copy link'}</span>
                        </button>

                        {isAudio && (
                            <>
                                <span className="fd-head-divider" aria-hidden="true" />
                                <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={handleSplit} title="Split into regions">
                                    <IconScissors />
                                    <span className="fd-action-label">Split</span>
                                </button>
                                {canEdit && (
                                    <button
                                        type="button"
                                        className="qd-btn qd-btn--ghost qd-btn--sm"
                                        onClick={() => setShowDenoiseModal(true)}
                                        title="Reduce background noise"
                                    >
                                        <IconSparkle />
                                        <span className="fd-action-label">Denoise</span>
                                    </button>
                                )}
                            </>
                        )}

                        <ActionMenu items={menuItems} label="More file actions" />

                        {position && (
                            <>
                                <span className="fd-head-divider" aria-hidden="true" />
                                <div className="fd-stepper">
                                    <button
                                        type="button"
                                        className="qd-btn qd-btn--ghost qd-btn--sm qd-btn--icon"
                                        onClick={() => step(-1)}
                                        disabled={!position.hasPrev}
                                        aria-label="Previous file (left arrow)"
                                        title="Previous file (←)"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="15 18 9 12 15 6" />
                                        </svg>
                                    </button>
                                    <span className="fd-stepper-position" aria-live="polite">
                                        {position.current} of {position.total}
                                    </span>
                                    <button
                                        type="button"
                                        className="qd-btn qd-btn--ghost qd-btn--sm qd-btn--icon"
                                        onClick={() => step(1)}
                                        disabled={!position.hasNext}
                                        aria-label="Next file (right arrow)"
                                        title="Next file (→)"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </button>
                                </div>
                            </>
                        )}

                        <ClosingX onClick={onClose} />
                    </div>
                </header>

                {contentUnavailable && (
                    <div className="fd-alert">
                        <InlineBanner variant="warning" icon={<IconAlert />}>
                            This file’s content couldn’t be reached. Its record is still here, but downloads
                            and previews are unavailable — it may have been removed from storage.
                        </InlineBanner>
                    </div>
                )}

                <div className="fd-body">
                    <section className={`fd-stage fd-stage--${kind}`} aria-label="File preview">
                        <FilePreview
                            url={downloadUrl}
                            name={name}
                            mimetype={mimetype}
                            textContent={file.content}
                            peaks={peaks}
                            peaksDuration={peaksDuration}
                            waitingForPeaks={peaksLoading}
                            onCaptureThumbnail={handleCaptureThumbnail}
                            unavailable={contentUnavailable}
                        />
                    </section>

                    <aside className="fd-rail">
                        <Tabs
                            tabs={tabs}
                            value={currentTab}
                            onChange={setActiveTab}
                            idPrefix={TAB_IDS}
                            label="File details sections"
                        />

                        <div
                            className="fd-tabpanel"
                            hidden={currentTab !== 'details'}
                            {...tabPanelProps(TAB_IDS, 'details')}
                        >
                            <section className="fd-block">
                                <h2 className="fd-block-title">Tags</h2>
                                <FileTagsSection
                                    file={file}
                                    allTags={allTags}
                                    canEdit={canEdit}
                                    onChanged={() => refreshFileData()}
                                />
                            </section>

                            <section className="fd-block">
                                <h2 className="fd-block-title">Projects</h2>
                                <FileProjects
                                    file={file}
                                    canEdit={canEdit}
                                    onChanged={() => refreshFileData()}
                                />
                            </section>

                            {/* Beside Projects on purpose: both answer "what is
                                this file related to?", and both are edited from
                                the same place someone is already looking. */}
                            <section className="fd-block">
                                <h2 className="fd-block-title">
                                    <IconLink width="13" height="13" />
                                    Source &amp; exports
                                </h2>
                                <FileDerivationSection
                                    file={file}
                                    canEdit={canEdit}
                                    onChanged={() => refreshFileData()}
                                />
                            </section>

                            {metadata.aiDescription && (
                                <section className="fd-block">
                                    <h2 className="fd-block-title">
                                        <IconSparkle width="13" height="13" />
                                        AI description
                                    </h2>
                                    <p className="fd-ai-description">{metadata.aiDescription}</p>
                                </section>
                            )}

                            <section className="fd-block">
                                <h2 className="fd-block-title">Metadata</h2>
                                <MetadataEditor
                                    metadata={metadata}
                                    hiddenKeys={HIDDEN_METADATA_KEYS}
                                    onSave={handleMetadataSave}
                                    onRemove={handleMetadataRemove}
                                    disabled={!canEdit}
                                />
                            </section>

                            {technical.length > 0 && (
                                <section className="fd-block fd-tech">
                                    <button
                                        type="button"
                                        className="fd-tech-toggle"
                                        onClick={() => setShowTechnical((v) => !v)}
                                        aria-expanded={showTechnical}
                                    >
                                        <span className={`fd-tech-caret ${showTechnical ? 'is-open' : ''}`.trim()} aria-hidden="true">›</span>
                                        Technical details
                                        <span className="fd-tech-count">{technical.length}</span>
                                    </button>
                                    {showTechnical && (
                                        <dl className="fd-tech-list">
                                            {technical.map((row) => (
                                                <div key={row.key} className="fd-tech-row">
                                                    <dt>{row.label}</dt>
                                                    <dd>{row.value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    )}
                                </section>
                            )}
                        </div>

                        <div
                            className="fd-tabpanel"
                            hidden={currentTab !== 'versions'}
                            {...tabPanelProps(TAB_IDS, 'versions')}
                        >
                            {/* Above the version list on purpose: this is where
                                someone is about to upload a new one, and a lock
                                that appears after the fact has missed its moment. */}
                            {canEdit && (
                                <div className="fd-lock">
                                    <FileLockBadge
                                        file={file}
                                        canAdmin={rankRole(activeWorkspaceRole) >= rankRole('admin')}
                                    />
                                </div>
                            )}
                            <FileVersionHistory
                                file={file}
                                onCountChange={onVersionsLoaded}
                                onVersionUploaded={reloadFileAndUrls}
                                onVersionRestored={reloadFileAndUrls}
                            />
                        </div>

                        {showTranscriptTab && (
                            <div
                                className="fd-tabpanel fd-tabpanel--flush"
                                hidden={currentTab !== 'transcript'}
                                {...tabPanelProps(TAB_IDS, 'transcript')}
                            >
                                <TranscriptPanel
                                    text={transcript}
                                    isImage={isImage}
                                    supported={canTranscribe(mimetype)}
                                    fileName={name}
                                    canRun={canEdit}
                                    onRun={handleRequestTranscription}
                                />
                            </div>
                        )}

                        <div
                            className="fd-tabpanel"
                            hidden={currentTab !== 'notes'}
                            {...tabPanelProps(TAB_IDS, 'notes')}
                        >
                            <NotesPanel file={file} onCountChange={onNotesLoaded} />
                        </div>

                        <div
                            className="fd-tabpanel"
                            hidden={currentTab !== 'similar'}
                            {...tabPanelProps(TAB_IDS, 'similar')}
                        >
                            {/* MOUNTED ONLY WHEN SELECTED, unlike its peers.
                                The others render hidden because their content is
                                already in hand; this one issues a similarity
                                search on mount, and running that for every file
                                anyone opens would be a fan-out of vector queries
                                nobody asked for. Keying on `file.id` also makes
                                clicking through results re-run the search for
                                the file you landed on. */}
                            {currentTab === 'similar' && (
                                <SimilarAssetsSection
                                    key={file.id}
                                    file={file}
                                    onOpenFile={(id) => refreshFileData(false, id)}
                                />
                            )}
                        </div>
                    </aside>
                </div>
            </Modal>

            {showDenoiseModal && (
                <DenoiseModal
                    file={file}
                    originalUrl={downloadUrl}
                    canApply={canEdit}
                    onApplied={reloadFileAndUrls}
                    onClose={() => setShowDenoiseModal(false)}
                />
            )}
        </>
    );
};

export default FileDetailsModal;
